import React from 'react';
import { View, ScrollView, TouchableOpacity, SafeAreaView } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Link } from 'expo-router';
import { Text } from '@/components/ui/text';
import { Card, CardContent } from '@/components/ui/card';
import { useTabAccess } from '@/hooks/useTabAccess';
import { buildTabConfig } from '@/navigation/tabConfig';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/navigation/routes';
import { brand } from '../../../src/design-system/theme/tokens';

const COLORS = {
  primary: brand.primary,
  surface: '#F9FAFB',
};

type MoreItem = {
  id: string;
  title: string;
  icon: string;
  route: string;
  visible: boolean;
  color: string;
};

export default function MoreIndexScreen() {
  const { t } = useTranslation();
  const {
    user,
    chatTitle,
    showDocuments,
    showStudents,
    showProgress,
    showPlatformAdmin,
    showSchoolManagement,
    isSchool,
    isSuper,
  } = useTabAccess();

  const chatLabel = chatTitle || t('navigation.chat');
  const { overflowTabs } = buildTabConfig({
    t,
    chatLabel,
    user: Boolean(user),
    showProgress: Boolean(showProgress),
    showDocuments: Boolean(showDocuments),
    showStudents: Boolean(showStudents),
    showMyDocuments: Boolean(user),
  });

  const overflowItems: MoreItem[] = overflowTabs
    .filter((tab) => tab.key !== 'library')
    .map((tab) => ({
      id: tab.key,
      title: tab.label,
      icon: tab.icon,
      route: tab.route,
      visible: true,
      color: COLORS.primary,
    }));

  const moreItems: MoreItem[] = [
    {
      id: 'platform-admin',
      title: t('navigation.platformAdmin'),
      icon: 'admin-panel-settings',
      route: ROUTES.TABS_MORE_PLATFORM_ADMIN,
      visible: showPlatformAdmin,
      color: COLORS.primary,
    },
    {
      id: 'library',
      title: t('navigation.library'),
      icon: 'folder',
      route: ROUTES.TABS_MORE_LIBRARY,
      visible: !isSuper,
      color: COLORS.primary,
    },
    {
      id: 'documents',
      title: t('navigation.documents'),
      icon: 'description',
      route: ROUTES.TABS_DOCUMENTS,
      visible: isSuper,
      color: COLORS.primary,
    },
    {
      id: 'school-management',
      title: isSchool ? t('navigation.schoolAdmin') : t('navigation.schoolManagement'),
      icon: 'school',
      route: ROUTES.TABS_MORE_SCHOOL_MANAGEMENT,
      visible: showSchoolManagement,
      color: COLORS.primary,
    },
    ...overflowItems,
  ].filter(item => item.visible);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.surface }}>
      <View className="bg-white px-6 py-4 border-b border-gray-200">
        <Text className="text-2xl font-bold text-foreground">{t('navigation.more')}</Text>
      </View>

      <ScrollView className="flex-1 p-4">
        <View className="gap-3">
          {moreItems.map((item) => {
            const cardContent = (
              <CardContent className="p-4">
                <View className="flex-row items-center">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-4"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <MaterialIcons
                      name={item.icon as keyof typeof MaterialIcons.glyphMap}
                      size={24}
                      color={item.color}
                    />
                  </View>
                  <View className="flex-1">
                    <Text className="text-base font-semibold text-foreground">
                      {item.title}
                    </Text>
                  </View>
                  <MaterialIcons name="chevron-right" size={24} color="#9ca3af" />
                </View>
              </CardContent>
            );

            return (
              <Card key={item.id}>
                <Link href={item.route} asChild>
                  <TouchableOpacity>
                    {cardContent}
                  </TouchableOpacity>
                </Link>
              </Card>
            );
          })}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
